import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { demoApi, DemoRunListItem } from '../api/demo';
import { authApi } from '../api/auth';
import { AppLayout } from '../components/AppLayout';
import { DemoRunStatus } from '../components/DemoRunStatus';
import { FlaskConical, RefreshCw, AlertTriangle, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

function formatTimestamp(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('it-IT');
}

function shortCommit(value: string | null): string {
  if (!value) return '—';
  return value.slice(0, 7);
}

function summaryCount(run: DemoRunListItem, key: string): number {
  const value = run.summary?.[key];
  return typeof value === 'number' ? value : 0;
}

/**
 * Elenco dei run demo (route `/demo`).
 *
 * Mostra dataset/versione, commit corto, stato, timestamp ed esiti
 * pass/block/fail. Le route sono protette: senza token la pagina reindirizza
 * al login senza nemmeno eseguire la query (il backend risponderebbe 401).
 */
export function DemoRunsPage() {
  const token = authApi.getToken();

  const { data: runs, isLoading, error, refetch } = useQuery({
    queryKey: ['demo-runs'],
    queryFn: () => demoApi.listRuns(),
    enabled: token !== null,
  });

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AppLayout
      breadcrumbs={[{ label: 'Demo casi reali' }]}
      logoIcon={<FlaskConical className="size-4 text-primary" />}
      topbarActions={
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="size-3.5" />
          Aggiorna
        </Button>
      }
      mainClassName="mail-utility-page"
    >
      <div className="max-w-6xl mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <FlaskConical className="size-5 text-primary" />
              Demo casi reali
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Run di valutazione su dataset sintetici e reali.
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center text-muted-foreground py-8">Caricamento...</div>
        ) : error ? (
          <Card className="border-danger/20">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <AlertTriangle className="size-8 text-danger" />
                <div>
                  <h3 className="text-lg font-semibold text-foreground">
                    Impossibile caricare i run
                  </h3>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {(error as Error).message || 'Errore di connessione al server.'}
                  </p>
                  <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
                    <RefreshCw className="size-3.5" />
                    Riprova
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : runs && runs.length > 0 ? (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dataset</TableHead>
                    <TableHead>Commit</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Avviato</TableHead>
                    <TableHead>Esiti</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runs.map((run) => (
                    <TableRow key={run.id} data-testid={`demo-run-row-${run.id}`}>
                      <TableCell className="font-medium">
                        <div className="text-foreground">{run.dataset_name}</div>
                        <div className="text-xs text-muted-foreground">
                          v{run.dataset_version} · {run.dataset_kind}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-muted-foreground">
                        {shortCommit(run.git_commit)}
                      </TableCell>
                      <TableCell>
                        <DemoRunStatus status={run.status} />
                      </TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap">
                        {formatTimestamp(run.started_at)}
                      </TableCell>
                      <TableCell>
                        <span className="text-xs whitespace-nowrap">
                          <span className="text-success font-medium">
                            {summaryCount(run, 'passed')} pass
                          </span>
                          <span className="text-muted-foreground"> / </span>
                          <span className="text-warning font-medium">
                            {summaryCount(run, 'blocked')} block
                          </span>
                          <span className="text-muted-foreground"> / </span>
                          <span className="text-danger font-medium">
                            {summaryCount(run, 'failed')} fail
                          </span>
                        </span>
                      </TableCell>
                      <TableCell>
                        <Link
                          to={`/demo/runs/${run.id}`}
                          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                        >
                          Dettaglio
                          <ChevronRight className="size-3.5" />
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-6 text-center text-muted-foreground">
              Nessun run disponibile
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
